/**
 * QQ模式 (Question to Question) 相关API
 * 问题拆解与可视化
 */

import Logger from "../utils/logger";
import { API_PATHS } from "./config";
import { getCurrentUser, getCustomClientSlug, requestModelByModeConfig } from "./utils";

/**
 * 提取HTML代码部分
 * 从AI响应中提取HTML代码
 * @param {string} response - AI的完整响应
 * @returns {Promise<string>} 提取的HTML代码
 */
export const extractQQHTMLCode = async (response) => {
  try {
    // 查找代码块 - 在```html 和 ``` 之间
    const codeBlockRegex = /```(?:html)?([\s\S]*?)```/g;
    const matches = [...response.matchAll(codeBlockRegex)];

    let code = "";

    if (matches.length > 0) {
      // 找到包含HTML的代码块
      let bestMatch = "";

      for (const match of matches) {
        const codeBlock = match[1].trim();

        // 如果代码块包含<!DOCTYPE html>或<html，优先选择
        if (
          codeBlock.includes("<!DOCTYPE html>") ||
          codeBlock.includes("<html") ||
          codeBlock.includes("<HTML")
        ) {
          // 如果找到的代码块比当前最佳匹配长，或当前没有最佳匹配，则更新
          if (codeBlock.length > bestMatch.length || bestMatch === "") {
            bestMatch = codeBlock;
          }
        }
      }

      // 如果没有找到含有HTML标记的代码块，就选择最长的代码块
      if (bestMatch === "") {
        bestMatch = matches.reduce((longest, match) => {
          return match[1].trim().length > longest.length
            ? match[1].trim()
            : longest;
        }, "");
      }

      Logger.debug("成功提取HTML代码块", {
        matchesCount: matches.length,
        bestMatchLength: bestMatch.length,
      });

      code = bestMatch;
    } else {
      // 如果没有找到代码块，尝试查找从<!DOCTYPE或<html开始的部分
      const htmlStartIndex = response.search(/<!DOCTYPE\s+html>|<html/i);
      if (htmlStartIndex !== -1) {
        Logger.debug("通过HTML标签提取代码");
        code = response.substring(htmlStartIndex).trim();
      } else {
        // 如果什么都找不到，返回整个响应内容，并在日志中警告
        Logger.warn("未能找到明确的HTML代码块，将整个响应作为代码处理");
        code = response;
      }
    }

    return code;
  } catch (error) {
    Logger.error("提取HTML代码失败", error);
    return response; // 失败时返回原始响应
  }
};

/**
 * 发送生成QQ问题拆解的请求
 * @param {string} prompt - 用户输入的问题
 * @param {function} onChunkReceived - 每当接收到一个文本块时的回调
 * @param {function} onComplete - 当完成整个响应时的回调
 * @param {function} onError - 发生错误时的回调
 * @param {boolean} freeMode - 是否使用免费模型与免费渠道
 */
export const streamQQAnalysis = async (
  prompt,
  onChunkReceived,
  onComplete,
  onError,
  freeMode = false
) => {
  Logger.info("开始QQ模式API调用", {
    endpoint: `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
    prompt,
  });

  try {
    // 构建请求体
    const requestBody = {
      mode: "qq", // QQ模式
      prompt: prompt, // 用户的问题
      stream: false, // 改为非流式响应
      free_mode: freeMode,
    };
    const slug = getCustomClientSlug();
    if (slug) requestBody.custom_client_slug = slug;

    Logger.debug("发送QQ模式API请求", requestBody);

    // 添加用户ID
    const currentUser = getCurrentUser();
    requestBody.userId = currentUser?.id || 0;

    const { fullResponse, chargeSession } = await requestModelByModeConfig(requestBody, onChunkReceived);

    Logger.debug("QQ模式API响应完成", { responseLength: fullResponse.length });

    // 提取HTML代码部分
    const htmlCode = await extractQQHTMLCode(fullResponse);

    Logger.info("QQ模式API调用完成，返回HTML代码", {
      responseLength: fullResponse.length,
      codeLength: htmlCode.length,
    });
    await onComplete(htmlCode, chargeSession);

    Logger.logApiCall(
      `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
      { prompt, mode: "qq" },
      "success",
      { codeLength: htmlCode.length }
    );
  } catch (error) {
    Logger.error("QQ模式API调用失败", error);
    Logger.logApiCall(
      `${API_PATHS.BASE_API_URL}${API_PATHS.PROXY.MODEL}`,
      { prompt, mode: "qq" },
      "error",
      { error: error.message }
    );
    const msg = error?.message?.includes("当前使用人数过多") ? "当前使用人数过多" : (error?.message || "问题拆解生成失败");
    onError(msg);
  }
};

export default {
  streamQQAnalysis,
  extractQQHTMLCode,
};

